import { MapPin, Phone, User, Package, Clock, RotateCcw, Pencil, Loader2, Truck } from 'lucide-react'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { OrderStatusBadge } from '@/pages/supplier/components/OrderStatusBadge'
import { ORDER_CHANGE_TYPE_LABELS, type OrderStatus } from '@/schemas/supplier'
import type { EmployeeManualOrder, ManualOrderRefundEligibility } from '@/api/employee-manual-order'
import { employeeManualOrderApi } from '@/api/employee-manual-order'
import { showError } from '@/lib/error'
import { usePermissionStore } from '@/stores/permission'
import { CityCascadeSelect, type CityCascadeValue } from '@/components/common/CityCascadeSelect'
import { ExpressTrackingTimeline } from './ExpressTrackingTimeline'

interface ManualOrderDetailDrawerProps {
  order: EmployeeManualOrder | null
  open: boolean
  onOpenChange: (open: boolean) => void
  scope?: string
  orderStatusList?: OrderStatus[]
  isStatusLoading?: boolean
  refundEligibility?: ManualOrderRefundEligibility
  onApplyRefund?: (order: EmployeeManualOrder) => void
  onFlagsUpdated?: () => void
  onShippingUpdated?: () => void
}

export function ManualOrderDetailDrawer({
  order,
  open,
  onOpenChange,
  scope,
  orderStatusList,
  isStatusLoading,
  refundEligibility,
  onApplyRefund,
  onFlagsUpdated,
  onShippingUpdated,
}: ManualOrderDetailDrawerProps) {
  const canUpdateFlags = usePermissionStore((state) => state.hasPermission('order:manual:update_flags'))
  const canUpdateShipping = usePermissionStore((state) => state.hasPermission('order:manual:update_shipping'))

  const [flagsForm, setFlagsForm] = useState({
    is_store: false,
    is_cash: false,
    shipping_partial: false,
    shipping_remark: '',
  })
  const [isSavingFlags, setIsSavingFlags] = useState(false)

  const [isEditingShipping, setIsEditingShipping] = useState(false)
  const [isSavingShipping, setIsSavingShipping] = useState(false)
  const [shippingForm, setShippingForm] = useState({
    real_name: '',
    user_phone: '',
    user_address: '',
  })
  // 省市区级联选择（编辑时重新选择，不回显旧地址）
  const [cityValue, setCityValue] = useState<CityCascadeValue | null>(null)

  useEffect(() => {
    if (order) {
      setFlagsForm({
        is_store: order.is_store ?? false,
        is_cash: order.is_cash ?? false,
        shipping_partial: order.shipping_partial ?? false,
        shipping_remark: order.shipping_remark ?? '',
      })
      setShippingForm({
        real_name: order.real_name || '',
        user_phone: order.user_phone || '',
        user_address: order.user_address || '',
      })
      setIsEditingShipping(false)
      setCityValue(null)
    }
  }, [order])

  const getChangeTypeLabel = (type: string) => {
    return ORDER_CHANGE_TYPE_LABELS[type] || type
  }

  const getPayTypeLabel = (manualOrder: EmployeeManualOrder) => {
    const labels: Record<string, string> = {
      weixin: '微信支付',
      wxpay: '微信支付',
      wechat: '微信支付',
      yue: '余额支付',
      balance: '余额支付',
      offline: '线下支付',
      alipay: '支付宝',
    }
    if (manualOrder.pay_type) return labels[manualOrder.pay_type] || manualOrder.pay_type
    return manualOrder.paid === 1 ? '已支付' : '未支付'
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>订单详情</SheetTitle>
          {order && <SheetDescription>订单号：{order.order_id}</SheetDescription>}
        </SheetHeader>

        {!order ? (
          <div className="space-y-6 py-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 py-3 border-b">
              <div>
                <span className="text-sm text-muted-foreground">订单状态</span>
                {refundEligibility && !refundEligibility.can_apply && refundEligibility.reason && (
                  <p className="mt-1 text-xs text-muted-foreground">{refundEligibility.reason}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <OrderStatusBadge status={order.status} refundType={order.refund_type} />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!refundEligibility?.can_apply || !onApplyRefund}
                  onClick={() => onApplyRefund?.(order)}
                >
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  申请退货
                </Button>
              </div>
            </div>

            <Tabs defaultValue="order" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="order">订单信息</TabsTrigger>
                <TabsTrigger value="products">商品信息</TabsTrigger>
                <TabsTrigger value="records">订单记录</TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[calc(100vh-260px)]">
                <TabsContent value="order" className="space-y-4 mt-4">
                  <div className="rounded-lg border bg-card p-4">
                    <h3 className="font-medium mb-3">基本信息</h3>
                    <div className="space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-muted-foreground">订单号：</span>
                          <span className="font-mono">{order.order_id}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">创建人：</span>
                          <span>{order.creator_name || '-'}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-muted-foreground">支付方式：</span>
                          <span>{getPayTypeLabel(order)}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-muted-foreground">下单时间：</span>
                          <span>{order.add_time}</span>
                        </div>
                        {order.paid_time && (
                          <div>
                            <span className="text-muted-foreground">支付时间：</span>
                            <span>{order.paid_time}</span>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-muted-foreground">商品数量：</span>
                          <span>{order.total_num} 件</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">订单金额：</span>
                          <span className="font-medium">¥{(order.total_price ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="flex justify-between font-medium text-base pt-2 border-t">
                        <span>实付金额：</span>
                        <span className="text-primary text-lg">¥{(order.pay_price ?? 0).toFixed(2)}</span>
                      </div>
                      {order.mark && (
                        <div>
                          <span className="text-muted-foreground">买家备注：</span>
                          <span>{order.mark}</span>
                        </div>
                      )}
                      {order.remark && (
                        <div>
                          <span className="text-muted-foreground">商家备注：</span>
                          <span>{order.remark}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border bg-card p-4">
                    <h3 className="font-medium flex items-center gap-2 mb-3">
                      <MapPin className="h-4 w-4" />
                      收货信息
                      {canUpdateShipping && !isEditingShipping && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-auto h-7 gap-1 px-2"
                          onClick={() => setIsEditingShipping(true)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          编辑
                        </Button>
                      )}
                    </h3>
                    {canUpdateShipping && isEditingShipping ? (
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <label htmlFor="shipping-real-name" className="text-sm font-medium">收货人</label>
                          <Input
                            id="shipping-real-name"
                            value={shippingForm.real_name}
                            maxLength={64}
                            onChange={(e) => setShippingForm(prev => ({ ...prev, real_name: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-1">
                          <label htmlFor="shipping-user-phone" className="text-sm font-medium">联系电话</label>
                          <Input
                            id="shipping-user-phone"
                            value={shippingForm.user_phone}
                            maxLength={18}
                            onChange={(e) => setShippingForm(prev => ({ ...prev, user_phone: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-sm font-medium">省市区</label>
                          <CityCascadeSelect onChange={setCityValue} />
                          <p className="text-xs text-muted-foreground">原地址：{order.user_address}</p>
                        </div>
                        <div className="space-y-1">
                          <label htmlFor="shipping-user-detail" className="text-sm font-medium">详细地址</label>
                          <Input
                            id="shipping-user-detail"
                            placeholder="街道、门牌号等"
                            maxLength={255}
                            value={shippingForm.user_address}
                            onChange={(e) => setShippingForm(prev => ({ ...prev, user_address: e.target.value }))}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isSavingShipping}
                            onClick={() => {
                              setIsEditingShipping(false)
                              if (order) {
                                setShippingForm({
                                  real_name: order.real_name || '',
                                  user_phone: order.user_phone || '',
                                  user_address: order.user_address || '',
                                })
                              }
                              setCityValue(null)
                            }}
                          >
                            取消
                          </Button>
                          <Button
                            size="sm"
                            disabled={isSavingShipping || !shippingForm.real_name.trim() || !shippingForm.user_phone.trim() || !cityValue || !shippingForm.user_address.trim()}
                            onClick={async () => {
                              if (!order || !cityValue) return
                              const fullAddress = `${cityValue.province}${cityValue.city}${cityValue.district}${shippingForm.user_address.trim()}`
                              setIsSavingShipping(true)
                              try {
                                await employeeManualOrderApi.updateShipping(order.id, {
                                  real_name: shippingForm.real_name.trim(),
                                  user_phone: shippingForm.user_phone.trim(),
                                  user_address: fullAddress,
                                })
                                toast.success('收货信息更新成功')
                                setIsEditingShipping(false)
                                onShippingUpdated?.()
                              } catch (error) {
                                showError(error, '收货信息更新失败')
                              } finally {
                                setIsSavingShipping(false)
                              }
                            }}
                          >
                            {isSavingShipping && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                            保存
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{order.real_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span>{order.user_phone}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <span>{order.user_address}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {order.delivery_id && (
                    <div className="rounded-lg border bg-card p-4">
                      <h3 className="mb-3 flex items-center gap-2 font-medium">
                        <Truck className="h-4 w-4" />
                        物流信息
                      </h3>
                      <ExpressTrackingTimeline
                        orderId={order.id}
                        scope={scope}
                        comName={order.delivery_name}
                        nu={order.delivery_id}
                      />
                    </div>
                  )}

                  {/* 订单标记 */}
                  <div className="space-y-3 rounded-lg border bg-card p-4">
                    <h3 className="text-sm font-medium">订单标记</h3>
                    {canUpdateFlags ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-4">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={flagsForm.is_store}
                              onCheckedChange={(checked) => setFlagsForm(prev => ({ ...prev, is_store: checked }))}
                            />
                            <span className="text-sm">仓库保管</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={flagsForm.is_cash}
                              onCheckedChange={(checked) => setFlagsForm(prev => ({ ...prev, is_cash: checked }))}
                            />
                            <span className="text-sm">货到付款</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={flagsForm.shipping_partial}
                              onCheckedChange={(checked) => setFlagsForm(prev => ({ ...prev, shipping_partial: checked }))}
                            />
                            <span className="text-sm">部分发货</span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">发货备注</span>
                          <Textarea
                            value={flagsForm.shipping_remark}
                            onChange={(e) => setFlagsForm(prev => ({ ...prev, shipping_remark: e.target.value }))}
                            rows={3}
                            maxLength={255}
                            placeholder="发货备注..."
                          />
                        </div>
                        <Button
                          size="sm"
                          disabled={isSavingFlags}
                          onClick={async () => {
                            if (!order) return
                            setIsSavingFlags(true)
                            try {
                              await employeeManualOrderApi.updateFlags(order.id, flagsForm)
                              toast.success('标记更新成功')
                              onFlagsUpdated?.()
                            } catch (error) {
                              showError(error, '标记更新失败')
                            } finally {
                              setIsSavingFlags(false)
                            }
                          }}
                        >
                          {isSavingFlags ? '保存中...' : '保存标记'}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {order.is_store && <Badge variant="outline" className="text-amber-600">仓库保管</Badge>}
                        {order.is_cash && <Badge variant="outline" className="text-red-600">货到付款</Badge>}
                        {order.shipping_partial && <Badge variant="outline" className="text-blue-600">部分发货</Badge>}
                        {!order.is_store && !order.is_cash && !order.shipping_partial && (
                          <span className="text-sm text-muted-foreground">无标记</span>
                        )}
                        {order.shipping_remark && (
                          <span className="text-sm text-muted-foreground">备注：{order.shipping_remark}</span>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="products" className="mt-4">
                  <div className="rounded-lg border bg-card p-4">
                    <h3 className="font-medium flex items-center gap-2 mb-4">
                      <Package className="h-4 w-4" />
                      商品列表
                    </h3>
                    <div className="space-y-4">
                      {order.cart_info?.map((item, index) => (
                        <div key={item.cart_id}>
                          {index > 0 && <Separator className="mb-4" />}
                          <div className="flex items-start gap-3">
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-16 h-16 object-cover rounded border"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.product_name}</p>
                              {item.product_attr && (
                                <p className="text-xs text-muted-foreground mt-0.5">{item.product_attr}</p>
                              )}
                              {item.sku && (
                                <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>
                              )}
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-sm">
                                  ¥{(item.price ?? 0).toFixed(2)}
                                </span>
                                <span className="text-sm text-muted-foreground">x{item.cart_num ?? 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-4" />
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">商品总数：</span>
                        <span>{order.total_num} 件</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">商品总价：</span>
                        <span>¥{(order.total_price ?? 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-medium text-base pt-2 border-t">
                        <span>实付金额：</span>
                        <span className="text-primary">¥{(order.pay_price ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="records" className="mt-4">
                  <div className="rounded-lg border bg-card p-4">
                    <h3 className="font-medium flex items-center gap-2 mb-4">
                      <Clock className="h-4 w-4" />
                      订单记录
                    </h3>
                    {isStatusLoading ? (
                      <div className="space-y-4">
                        {[1, 2, 3].map((index) => (
                          <div key={index} className="flex gap-3">
                            <Skeleton className="h-2 w-2 rounded-full mt-2" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-4 w-24" />
                              <Skeleton className="h-3 w-40" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : orderStatusList && orderStatusList.length > 0 ? (
                      <div className="relative">
                        <div className="absolute left-1.5 top-2 bottom-2 w-px bg-border" />
                        <div className="space-y-4">
                          {orderStatusList.map((status) => (
                            <div key={status.id} className="flex gap-3 relative">
                              <div className="w-3 h-3 rounded-full bg-primary mt-1 z-10" />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm">
                                    {getChangeTypeLabel(status.change_type)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {status.change_time}
                                  </span>
                                </div>
                                {status.change_message && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {status.change_message}
                                  </p>
                                )}
                                {status.change_manager && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    操作人：{status.change_manager}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">暂无订单记录</div>
                    )}
                  </div>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
